import { currentKp, currentWind, weatherDomain } from '../utils/observations.js';
import { useRef } from 'react';

// Sun-to-Earth hero visualization. Pure SVG + animated CSS particles.
// Aurora oval opacity scales with Kp; particle density with solar wind speed.

export default function SolarHero({ weather, cmes = [] }) {
  const kp     = currentKp(weather);
  const bz     = currentWind(weather,'bz','mag');
  const wind   = currentWind(weather,'speed_kms_raw');
  const dens   = currentWind(weather,'density');
  const inboundCme = cmes.find(c => c.inbound && c.hours_to_arrival != null && c.hours_to_arrival > 0);

  const particleCount = wind == null ? 0 : Math.min(120, Math.max(30, Math.round(wind / 6)));

  const particles = useRef([]);
  if (particles.current.length === 0) {
    for (let i = 0; i < 120; i++) {
      particles.current.push({
        leftPct: 14 + Math.random() * 6,
        topPct:  10 + Math.random() * 80,
        delay:   -Math.random() * 10,
        dur:     5 + Math.random() * 5,
        fast:    Math.random() < 0.25,
        dense:   Math.random() < 0.2,
      });
    }
  }

  const auroraOpacity = Math.min(0.85, (kp == null ? 0 : 0.2 + kp * 0.08));

  return (
    <div className="hg-hero">
      <div className="hg-hero-head">

      <div className="hg-hero-tag">
        <b>SUN, EARTH</b><br />
        HELIOSPHERIC PLANE · L1 UPSTREAM · NOT TO SCALE
      </div>
      <div className="hg-hero-stamp">
        <span>NOAA · source observations</span>
        <span className="pill">{['kp','wind','mag'].every(k=>weatherDomain(weather,k).status === 'fresh') ? 'CURRENT' : 'PARTIAL / UNAVAILABLE'}</span>
      </div>

      </div>
      <div className="hg-scene-frame">
      <svg className="scene" viewBox="0 0 1180 622" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="sunCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#fff3d4" />
            <stop offset="35%"  stopColor="#f5c674" />
            <stop offset="70%"  stopColor="#d68633" />
            <stop offset="100%" stopColor="#7a2c10" />
          </radialGradient>
          <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="rgba(233,179,93,0.42)" />
            <stop offset="40%"  stopColor="rgba(233,179,93,0.14)" />
            <stop offset="100%" stopColor="rgba(233,179,93,0)" />
          </radialGradient>
          <radialGradient id="earthCore" cx="40%" cy="35%" r="65%">
            <stop offset="0%"   stopColor="#3a5a7a" />
            <stop offset="55%"  stopColor="#1a2a3e" />
            <stop offset="100%" stopColor="#070a10" />
          </radialGradient>
          <radialGradient id="magneto" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="rgba(127,174,200,0)" />
            <stop offset="70%"  stopColor="rgba(127,174,200,0.07)" />
            <stop offset="100%" stopColor="rgba(127,174,200,0)" />
          </radialGradient>
          <linearGradient id="auroraGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="rgba(166,152,216,0)" />
            <stop offset="50%"  stopColor="rgba(166,152,216,0.85)" />
            <stop offset="100%" stopColor="rgba(166,152,216,0)" />
          </linearGradient>
          <filter id="soft" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" />
          </filter>
        </defs>

        {/* horizon hairlines */}
        <g stroke="rgba(232,227,216,0.035)" strokeWidth="0.5" fill="none">
          <line x1="0" y1="311" x2="1180" y2="311" />
          <line x1="0" y1="200" x2="1180" y2="200" />
          <line x1="0" y1="422" x2="1180" y2="422" />
        </g>

        {/* SUN */}
        <g transform="translate(170,311)">
          <circle r="220" fill="url(#sunGlow)" />
          <circle r="148" fill="none" stroke="rgba(233,179,93,0.14)" strokeWidth="0.5" strokeDasharray="2 3" />
          <circle r="124" fill="none" stroke="rgba(233,179,93,0.20)" strokeWidth="0.5" />
          <circle r="108" fill="none" stroke="rgba(233,179,93,0.28)" strokeWidth="0.5" />
          <circle r="92"  fill="url(#sunCore)" />
          <circle r="92"  fill="none" stroke="rgba(255,220,160,0.30)" strokeWidth="0.5" />
          <g className="hg-sun-spin">
            <g transform="translate(30,-14)">
              <circle r="9"   fill="rgba(40,16,8,0.85)" />
              <circle r="3.5" cx="-3" cy="2"  fill="rgba(20,8,4,0.92)" />
              <circle r="2.5" cx="4"  cy="-3" fill="rgba(20,8,4,0.85)" />
            </g>
            <g transform="translate(-24,20)">
              <circle r="5"   fill="rgba(40,16,8,0.7)" />
              <circle r="1.8" fill="rgba(20,8,4,0.85)" />
            </g>
            <g transform="translate(40,32)">
              <circle r="3" fill="rgba(40,16,8,0.55)" />
            </g>
            <ellipse cx="-34" cy="-36" rx="14" ry="9" fill="rgba(0,0,0,0.32)" transform="rotate(-30 -34 -36)" />
          </g>
        </g>

        {/* streamlines */}
        <g stroke="rgba(233,179,93,0.08)" strokeWidth="0.5" fill="none">
          <path d="M 290 250 Q 600 220 940 270" />
          <path d="M 290 311 Q 600 311 940 311" />
          <path d="M 290 372 Q 600 400 940 352" />
          <path d="M 290 200 Q 600 170 940 222" />
          <path d="M 290 422 Q 600 450 940 400" />
        </g>

        {/* L1 marker */}
        <g transform="translate(740,290)">
          <line x1="0" y1="-22" x2="0" y2="22" stroke="rgba(127,174,200,0.45)" strokeWidth="0.5" strokeDasharray="2 2" />
          <circle r="14" fill="none" stroke="rgba(127,174,200,0.22)" />
          <circle r="7"  fill="none" stroke="rgba(127,174,200,0.55)" />
          <rect x="-3" y="-3" width="6" height="6" fill="var(--cyan)" />
        </g>

        {/* EARTH + magnetosphere + aurora */}
        <g transform="translate(985,311)">
          <path d="M 90 -90 Q 70 -150 0 -130 Q -60 -110 -90 -60 L -110 -10 L -90 60 Q -60 110 0 130 Q 70 150 90 90 L 170 30 Q 200 0 170 -30 Z"
                fill="url(#magneto)" stroke="rgba(127,174,200,0.16)" strokeWidth="0.5" />
          <path d="M -88 0 Q -100 -160 60 -210 M -88 0 Q -100 160 60 210"
                fill="none" stroke="rgba(127,174,200,0.40)" strokeWidth="0.6" strokeDasharray="3 3" />
          <path d="M -64 0 Q -78 -120 40 -156 M -64 0 Q -78 120 40 156"
                fill="none" stroke="rgba(127,174,200,0.7)" strokeWidth="0.7" />
          <g stroke="rgba(127,174,200,0.32)" strokeWidth="0.4" fill="none">
            <path d="M 0 -42 Q -32 -28 -52 0 Q -32 28 0 42" />
            <path d="M 0 -42 Q  32 -28  52 0 Q  32 28 0 42" />
          </g>
          <circle r="42" fill="url(#earthCore)" />
          <path d="M 0 -42 A 42 42 0 0 1 0 42 A 24 42 0 0 0 0 -42" fill="rgba(255,255,255,0.04)" />
          <g stroke="rgba(127,174,200,0.18)" strokeWidth="0.4" fill="none">
            <ellipse rx="42" ry="14" />
            <ellipse rx="42" ry="28" />
            <line x1="-42" y1="0" x2="42" y2="0" />
            <line x1="0" y1="-42" x2="0" y2="42" />
          </g>
          {/* aurora oval, opacity tied to Kp */}
          <ellipse cx="0" cy="-28" rx="30" ry="9" fill="none" stroke="url(#auroraGrad)" strokeWidth="2.5"   opacity={auroraOpacity} />
          <ellipse cx="0" cy="-28" rx="30" ry="9" fill="none" stroke="rgba(166,152,216,0.35)" strokeWidth="6" filter="url(#soft)" opacity={auroraOpacity} />
          <ellipse cx="0" cy="28"  rx="30" ry="9" fill="none" stroke="url(#auroraGrad)" strokeWidth="2"     opacity={auroraOpacity * 0.8} />
        </g>

      </svg>
      {/* particle stream */}
      <div className="hg-stream">
        {particles.current.slice(0, particleCount).map((p, i) => (
          <div
            key={i}
            className={`hg-particle${p.fast ? ' fast' : ''}${p.dense ? ' dense' : ''}`}
            style={{
              left: `${p.leftPct}%`,
              top:  `${p.topPct}%`,
              animationDuration: `${p.dur}s`,
              animationDelay:    `${p.delay}s`,
            }}
          />
        ))}
      </div>
      </div>
      <div className="hg-legend" aria-label="Sun to Earth diagram labels">
        <div className="hg-vlabel amber"><b>SUN</b>1.39 × 10⁶ km · G2V</div>
        <div className="hg-vlabel"><b>{wind != null ? `${Math.round(wind)} km/s` : '- km/s'}</b>SOLAR WIND{dens != null ? ` · ${dens.toFixed(1)} p/cm³` : ''}</div>
        <div className="hg-vlabel cyan"><b>L1 · DSCOVR</b>{inboundCme ? `CME ETA ${inboundCme.hours_to_arrival.toFixed(1)}h` : 'Solar wind monitor'}</div>
        <div className="hg-vlabel"><b>EARTH</b>Bz = {bz != null ? `${bz.toFixed(1)} nT` : '-'} · Kp {kp == null ? '-' : kp.toFixed(1)}</div>
        <div className="hg-vlabel violet"><b>AURORA OVAL</b>{kp == null ? 'Observations unavailable' : kp >= 5 ? '50°N, 70°N' : kp >= 3 ? '60°N, 75°N' : '70°N+'}</div>
      </div>


    </div>
  );
}
